import AutoModeIcon from '@mui/icons-material/AutoMode'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoIcon from '@mui/icons-material/InfoOutline'
import PersonIcon from '@mui/icons-material/Person'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Switch,
  Tooltip,
  Typography
} from '@mui/material'
import { FraudFlagDto } from '@src/data/api/fraud/fraud-api'
import { useUpdateFraudFlag } from '@src/data/api/fraud/useFraud'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FraudFlagReasonDialog } from './FraudFlagReasonDialog'

interface FraudFlagCardProps {
  flag: FraudFlagDto
  indicatorType: string
  financingProgramId: string
  creditApplicationId: string
  currentLanguage: string
  displayCategory?: boolean
}

export const FraudFlagCard: React.FC<FraudFlagCardProps> = ({ flag, indicatorType, financingProgramId, creditApplicationId, currentLanguage, displayCategory }) => {
  const { t } = useTranslation()
  const [updateFraudFlag, isUpdatingFlag] = useUpdateFraudFlag()
  const [showReasonDialog, setShowReasonDialog] = useState(false)
  const [reason, setReason] = useState('')
  const [pendingValue, setPendingValue] = useState(false)
  const definition = t(`fraudFlags.flag.${flag.key}.definition`, t('fraudFlags.flag.default.definition'))
  const flagReason = useMemo(() => {
    if (flag.key === 'name_to_review' && flag.reason) {
      // For the "name_to_review" flag, the reason is stored in a specific format and needs to be translated accordingly
      // The reason is expected to be "Smith_review" or "son_suffix" or "missing_name" or "equal_name" otherwise it will fallback to default reason text
      const valueReason = flag.reason?.split('_', 2)
      return t(`fraudFlags.flag.${flag.key}.reason.${valueReason[1]}`,
        {
          defaultValue: t(`fraudFlags.flag.${flag.key}.reason.${flag.reason}`, flag.reason),
          value: valueReason[0]
        })
    }
    return flag.reason
  }, [flag.key, flag.reason, t])

  const handleToggle = () => {
    setPendingValue(!flag.value)
    setShowReasonDialog(true)
  }

  const handleConfirmToggle = async () => {
    try {
      await updateFraudFlag({
        financingProgramId,
        creditApplicationId,
        indicatorType,
        flagKey: flag.key,
        request: {
          value: pendingValue,
          reason,
        },
      })
      setShowReasonDialog(false)
      setReason('')
    } catch (error) {
      console.error('Failed to update flag:', error)
    }
  }

  const handleCancelToggle = () => {
    setShowReasonDialog(false)
    setReason('')
    setPendingValue(flag.value)
  }

  const displayName = currentLanguage === 'fr' ? flag.displayNameFr : flag.displayNameEn

  return (
    <>
      <Card
        sx={{
          height: '100%',
          borderLeft: 4,
          borderLeftColor: flag.value ? 'error.main' : 'divider',
          bgcolor: flag.value ? 'error.50' : 'background.paper',
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="subtitle2" component="div" sx={{ flex: 1 }}>
                {displayName}
                <Tooltip title={definition} placement="top">
                  <InfoIcon sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                </Tooltip>
              </Typography>
              <Tooltip title={flag.value ? t('fraudFlags.detected') : t('fraudFlags.notDetected')} placement="bottom">
                {flag.value ? (
                  <WarningIcon color="error" fontSize="small" />
                ) : (
                  <CheckCircleIcon color="success" fontSize="small" />
                )}
              </Tooltip>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                {displayCategory && <Chip color='primary' size='small' variant="outlined" label={t(`fraudFlags.categories.${flag.category}`, { defaultValue: flag.category })} />}
                <Chip
                  size="small"
                  icon={flag.isAutomatic ? <AutoModeIcon /> : <PersonIcon />}
                  label={flag.isAutomatic ? t('fraudFlags.automatic') : t('fraudFlags.manual')}
                  variant={flag.isAutomatic ? 'filled' : 'outlined'}
                />
              </Stack>

              <Tooltip title={flag.isAutomatic ? t('fraudFlags.automaticFlagTooltip') : ''} placement="top">
                <Box>
                  <Switch
                    checked={flag.value}
                    onChange={handleToggle}
                    disabled={isUpdatingFlag}
                    color={flag.value ? 'error' : 'default'}
                  />
                </Box>
              </Tooltip>
            </Box>

            {flag.reason && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="caption" color="text.primary" display="block" fontWeight="medium">
                  {t('fraudFlags.reasonLabel')}:
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: "pre-line" }}>
                  {flagReason}
                </Typography>
              </Box>
            )}

            {flag.lastModified && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('fraudFlags.lastModified')}: {format(new Date(flag.lastModified), 'dd/MM/yyyy HH:mm')}
                </Typography>
                {flag.modifiedByName && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('fraudFlags.modifiedBy')}: {flag.modifiedByName}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <FraudFlagReasonDialog
        showReasonDialog={showReasonDialog}
        pendingValue={pendingValue}
        displayName={displayName}
        isUpdatingFlag={isUpdatingFlag}
        reason={reason}
        setReason={setReason}
        handleConfirmToggle={handleConfirmToggle}
        handleCancelToggle={handleCancelToggle}
      />
    </>
  )
}
